import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toPng } from 'html-to-image';
import { X, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from 'recharts';
import './PerformanceCard.css';

const API = process.env.REACT_APP_BACKEND_URL;

const pctBand = (p) => (p === undefined || p === null ? '' : p >= 100 ? 'pc-g' : p >= 75 ? 'pc-a' : 'pc-r');

function useCountUp(target, duration = 900, start = true) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!start || target === undefined || target === null) return;
    let raf; const t0 = performance.now();
    const tick = (t) => {
      const k = Math.min(1, (t - t0) / duration);
      setVal(Math.round(target * (1 - Math.pow(1 - k, 3))));
      if (k < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, start]);
  return val;
}

function PctChip({ pct, display, delay = 0 }) {
  const v = useCountUp(pct);
  if (display !== undefined) return <span className="pc-chip pc-neutral" style={{ animationDelay: `${delay}ms` }}>{display}</span>;
  return <span className={`pc-chip ${pctBand(pct)}`} style={{ animationDelay: `${delay}ms` }}>{v}%</span>;
}

function MetricRow({ m, i }) {
  return (
    <div className="pc-metric" style={{ animationDelay: `${120 + i * 55}ms` }}>
      <span className="pc-metric-label">{m.label}</span>
      <span className="pc-metric-value">{m.value}</span>
      <PctChip pct={m.pct} display={m.pct_display} delay={120 + i * 55} />
    </div>
  );
}

function GaugeBar({ label, value, highlight, i }) {
  const v = useCountUp(value);
  return (
    <div className="pc-gauge" style={{ animationDelay: `${350 + i * 80}ms` }}>
      <div className="pc-gauge-top"><span>{label}</span><b className={highlight ? 'pc-blue' : ''}>{v}%</b></div>
      <div className="pc-gauge-track">
        <div
          className={`pc-gauge-fill ${highlight ? 'is-blue' : pctBand(value).replace('pc-', 'is-')}`}
          style={{ width: `${Math.min(100, value)}%`, transitionDelay: `${400 + i * 80}ms` }}
        />
      </div>
    </div>
  );
}

export default function PerformanceCard({ memberId, onClose, embedded = false }) {
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [mounted, setMounted] = useState(false);
  const bodyRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/staff/performance/${memberId}`);
        setCard(r.data);
        requestAnimationFrame(() => setMounted(true));
      } catch (e) {
        toast.error(e.response?.data?.detail || 'No performance card available');
        if (onClose) onClose();
      }
      setLoading(false);
    })();
  }, [memberId, onClose]);

  const doExport = useCallback(async () => {
    if (!bodyRef.current) return;
    setExporting(true);
    await new Promise((r) => setTimeout(r, 60));
    try {
      const dataUrl = await toPng(bodyRef.current, { backgroundColor: '#3d3d3d', pixelRatio: 2, includeQueryParams: true });
      const a = document.createElement('a');
      a.download = `${card.name.replace(/\s+/g, '-')}-Performance-${card.period.replace(/\s+/g, '-')}.png`;
      a.href = dataUrl;
      a.click();
      toast.success('Performance card downloaded');
    } catch (e) {
      toast.error('Export failed');
    }
    setExporting(false);
  }, [card]);

  if (loading) {
    if (embedded) return <div className="pc-loading-inline"><Loader2 size={24} className="pc-spin" /></div>;
    return (
      <div className="pc-overlay"><div className="pc-loading"><Loader2 size={24} className="pc-spin" /></div></div>
    );
  }
  if (!card) return embedded ? <div className="pc-loading-inline">No performance card available yet.</div> : null;

  const grade = card.grade;

  const inner = (
    <>
        <div className="pc-topbar">
          <button className="pc-btn" onClick={doExport} disabled={exporting} data-testid="pc-download">
            {exporting ? <Loader2 size={13} className="pc-spin" /> : <Download size={13} />} PNG
          </button>
          {!embedded && <button className="pc-btn pc-close" onClick={onClose} data-testid="pc-close"><X size={15} /></button>}
        </div>

        <div className="pc-body" ref={bodyRef}>
          {/* Header */}
          <div className="pc-header">
            <div className="pc-id">
              <div className="pc-photo"><img src={`${API}${card.photo}`} alt={card.name} /></div>
              <div>
                <h2 className="pc-name">{card.name}</h2>
                <div className="pc-team">{card.team}</div>
                <div className="pc-title-row">
                  <span className="pc-badge">{card.title}</span>
                  <span className="pc-badge pc-badge-grade" data-testid="pc-grade">
                    Grade
                    <span className="pc-grade-dots">
                      {Array.from({ length: 9 }).map((_, i) => (
                        <i key={i} className={i < grade ? 'on' : ''} style={{ animationDelay: `${250 + i * 60}ms` }} />
                      ))}
                    </span>
                    <b>{grade}</b>
                  </span>
                  <span className="pc-badge">Bonus rate <b>{card.bonus_rate}</b></span>
                </div>
              </div>
            </div>
            <div className="pc-period">{card.period}</div>
          </div>

          {/* Month metrics */}
          <div className="pc-columns">
            <div className="pc-panel">
              <h4>Month performance</h4>
              {card.month_metrics.map((m, i) => <MetricRow key={m.label} m={m} i={i} />)}
            </div>
            <div className="pc-panel">
              <h4>Production</h4>
              {card.month_metrics_right.map((m, i) => <MetricRow key={m.label} m={m} i={i} />)}
            </div>
            <div className="pc-panel pc-panel-bonus">
              <h4>Bonus</h4>
              <div className="pc-bonus-line" style={{ animationDelay: '160ms' }}><span>core bonus</span><b>{card.bonus.core}</b></div>
              <div className="pc-bonus-line" style={{ animationDelay: '220ms' }}><span>extra bonus (-/+)</span><b>{card.bonus.extra}</b></div>
              <div className="pc-bonus-line pc-bonus-total" style={{ animationDelay: '280ms' }}><span>total bonus</span><b data-testid="pc-total-bonus">{card.bonus.total}</b></div>
              <h4 className="pc-mt">Uploads within SLA</h4>
              {card.uploads.map((u, i) => <GaugeBar key={u.label} label={u.label} value={u.value} i={i} />)}
              <h4 className="pc-mt">Visits</h4>
              {card.sla.map((u, i) => <GaugeBar key={u.label} label={u.label} value={u.value} highlight={u.highlight} i={i + 3} />)}
            </div>
          </div>

          {/* YTD */}
          <div className="pc-panel pc-ytd">
            <h4>Year to Date 2026</h4>
            <div className="pc-ytd-grid pc-ytd-head">
              <span></span><span>2025 actual</span><span>2026 forecast</span><span>2026 actual</span><span>+/- %</span>
            </div>
            {card.ytd.map((r, i) => (
              <div key={r.label} className="pc-ytd-grid pc-ytd-row" style={{ animationDelay: `${200 + i * 60}ms` }}>
                <span className="pc-ytd-label">{r.label}</span>
                <span>{r.a2025}</span>
                <span className="pc-dim">{r.f2026}</span>
                <span className="pc-strong">{r.a2026}</span>
                <PctChip pct={r.pct} delay={200 + i * 60} />
              </div>
            ))}
          </div>

          {/* Charts */}
          <div className="pc-charts">
            <div className="pc-panel pc-chart">
              <h4>Value Per Day</h4>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={card.value_per_day} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke="rgba(255,255,255,0.08)" vertical={false} />
                  <XAxis dataKey="m" tick={{ fill: '#bbb', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: '#bbb', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => `£${v}`} />
                  <Tooltip contentStyle={{ background: '#2b2b2b', border: 'none', borderRadius: 8, fontSize: 12 }} formatter={(v) => [`£${v}`]} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line name="2025 actual" dataKey="a2025" stroke="#9a9a9a" strokeWidth={2} dot={false} animationDuration={1200} />
                  <Line name="2026 forecast" dataKey="f2026" stroke="#e8e8e8" strokeWidth={2} strokeDasharray="5 4" dot={false} animationDuration={1200} />
                  <Line name="2026 actual" dataKey="a2026" stroke="#96C11F" strokeWidth={3} dot={{ r: 3, fill: '#96C11F' }} animationDuration={1500} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="pc-panel pc-chart">
              <h4>Core Monthly Bonus</h4>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={card.monthly_bonus} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke="rgba(255,255,255,0.08)" vertical={false} />
                  <XAxis dataKey="m" tick={{ fill: '#bbb', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: '#bbb', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => `£${v}`} />
                  <Tooltip contentStyle={{ background: '#2b2b2b', border: 'none', borderRadius: 8, fontSize: 12 }} formatter={(v) => [`£${v}`]} cursor={{ fill: 'rgba(255,255,255,0.05)' }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar name="2025 actual" dataKey="a2025" fill="#9a9a9a" radius={[3, 3, 0, 0]} animationDuration={1200} />
                  <Bar name="2026 forecast" dataKey="f2026" fill="#e8e8e8" radius={[3, 3, 0, 0]} animationDuration={1200} />
                  <Bar name="2026 actual" dataKey="a2026" fill="#96C11F" radius={[3, 3, 0, 0]} animationDuration={1500} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
    </>
  );

  if (embedded) {
    return <div className={`pc-embedded ${mounted ? 'is-in' : ''}`}>{inner}</div>;
  }
  return (
    <div className="pc-overlay" onClick={onClose} data-testid="performance-card">
      <div className={`pc-sheet ${mounted ? 'is-in' : ''}`} onClick={(e) => e.stopPropagation()}>
        {inner}
      </div>
    </div>
  );
}
